/* eslint-disable no-console */

/**
 * Theme Manager Utility
 * Handles light/dark theme switching and persistent storage
 */

export class ThemeManager {
  constructor () {
    this.currentTheme = 'light';
    this.themeStorageKey = 'theme';
    this.themes = {
      light: 'light-theme',
      dark: 'dark-theme'
    };

    this.themeColors = {
      light: {
        '--primary-color': '#d41e2a',
        '--background-color': '#ffffff',
        '--surface-color': '#f8f9fa',
        '--text-color': '#212529',
        '--text-color-secondary': '#666666',
        '--border-color': '#e9ecef',
        '--suva-red': '#d41e2a',
        '--suva-red-dark': '#b31d29',
        '--suva-red-light': '#e53e3e',
        '--swiss-blue': '#0066cc',
        '--swiss-gray': '#666666',
        '--swiss-gray-light': '#f8f9fa',
        '--swiss-gray-medium': '#e9ecef',
        '--swiss-gray-dark': '#343a40'
      },
      dark: {
        '--primary-color': '#e53e3e',
        '--background-color': '#1a1a1a',
        '--surface-color': '#2d2d2d',
        '--text-color': '#ffffff',
        '--text-color-secondary': '#b0b0b0',
        '--border-color': '#404040',
        '--suva-red': '#e53e3e',
        '--suva-red-dark': '#c53030',
        '--suva-red-light': '#fc8181',
        '--swiss-blue': '#4299e1',
        '--swiss-gray': '#718096',
        '--swiss-gray-light': '#1a202c',
        '--swiss-gray-medium': '#2d3748',
        '--swiss-gray-dark': '#e2e8f0'
      }
    };
  }

  /**
   * Initialize theme manager
   */
  init (theme = null) {
    try {
      console.log('🎨 Initializing Theme Manager...');

      // Load saved theme or detect system preference
      this.currentTheme = theme || this.loadSavedTheme() || this.detectSystemTheme();

      // Apply theme
      this.applyTheme(this.currentTheme);

      // Setup system theme change listener
      this.setupSystemThemeListener();

      console.log(`✅ Theme initialized: ${this.currentTheme}`);
      return true;
    } catch (error) {
      console.error('❌ Theme initialization failed:', error);
      return false;
    }
  }

  /**
   * Apply theme to document
   */
  applyTheme (theme) {
    if (!this.themes[theme]) {
      console.warn(`Unknown theme: ${theme}`);
      return;
    }

    // Remove all theme classes
    Object.values(this.themes).forEach(themeClass => {
      document.body.classList.remove(themeClass);
    });

    // Add new theme class
    document.body.classList.add(this.themes[theme]);

    // Apply custom properties
    const customProperties = this.themeColors[theme];
    if (customProperties) {
      Object.entries(customProperties).forEach(([property, value]) => {
        document.documentElement.style.setProperty(property, value);
      });
    }

    // Update meta theme-color for PWA
    this.updateMetaThemeColor(theme);

    // Save to localStorage
    this.saveTheme(theme);

    // Update current theme
    this.currentTheme = theme;

    // Dispatch theme change event
    this.dispatchThemeChange(theme);
  }

  /**
   * Toggle between light and dark themes
   */
  toggleTheme () {
    const newTheme = this.currentTheme === 'light' ? 'dark' : 'light';
    this.applyTheme(newTheme);
    return newTheme;
  }

  /**
   * Set specific theme
   */
  setTheme (theme) {
    if (this.themes[theme]) {
      this.applyTheme(theme);
      return true;
    }
    return false;
  }

  /**
   * Get current theme
   */
  getTheme () {
    return this.currentTheme;
  }

  /**
   * Check if dark theme is active
   */
  isDarkTheme () {
    return this.currentTheme === 'dark';
  }

  /**
   * Get theme colors for current theme
   */
  getThemeColors (theme = null) {
    const targetTheme = theme || this.currentTheme;
    return { ...this.themeColors[targetTheme] };
  }

  /**
   * Get CSS variable value
   */
  getCSSVariable (variable) {
    if (typeof document !== 'undefined') {
      return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
    }
    return this.themeColors[this.currentTheme][variable] || '';
  }

  /**
   * Load saved theme from localStorage
   */
  loadSavedTheme () {
    try {
      if (typeof localStorage !== 'undefined') {
        return localStorage.getItem(this.themeStorageKey);
      }
    } catch (error) {
      console.warn('Could not load theme from localStorage:', error);
    }
    return null;
  }

  /**
   * Save theme to localStorage
   */
  saveTheme (theme) {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.themeStorageKey, theme);
      }
    } catch (error) {
      console.warn('Could not save theme to localStorage:', error);
    }
  }

  /**
   * Detect system theme preference
   */
  detectSystemTheme () {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? 'dark' : 'light';
    }
    return 'light'; // Default to light theme
  }

  /**
   * Setup system theme change listener
   */
  setupSystemThemeListener () {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      mediaQuery.addEventListener('change', (e) => {
        // Only change theme if user hasn't explicitly set one
        const savedTheme = this.loadSavedTheme();
        if (!savedTheme) {
          const newTheme = e.matches ? 'dark' : 'light';
          this.applyTheme(newTheme);
        }
      });
    }
  }

  /**
   * Update meta theme-color for PWA
   */
  updateMetaThemeColor (theme) {
    if (typeof document !== 'undefined') {
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        const color = this.themeColors[theme]['--primary-color'];
        metaThemeColor.setAttribute('content', color);
      }
    }
  }

  /**
   * Dispatch custom theme change event
   */
  dispatchThemeChange (theme) {
    if (typeof document !== 'undefined') {
      const event = new CustomEvent('themeChanged', {
        detail: {
          theme,
          colors: this.themeColors[theme],
          isDark: theme === 'dark'
        }
      });
      document.dispatchEvent(event);
    }
  }

  /**
   * Register theme toggle button
   */
  registerToggleButton (button) {
    if (!button) return;

    // Update button state
    this.updateToggleButton(button);

    // Add click handler
    button.addEventListener('click', () => {
      this.toggleTheme();
      this.updateToggleButton(button);
    });

    // Listen for theme changes
    document.addEventListener('themeChanged', () => {
      this.updateToggleButton(button);
    });
  }

  /**
   * Update toggle button state
   */
  updateToggleButton (button) {
    const isDark = this.isDarkTheme();

    // Update button text if needed
    const textElement = button.querySelector('span');
    if (textElement) {
      textElement.textContent = isDark ? 'light_mode' : 'dark_mode';
    }

    // Update button title
    button.title = isDark ? 'Light Mode' : 'Dark Mode';

    // Update button active state
    button.classList.toggle('active', isDark);
  }

  /**
   * Theme-aware color utilities
   */
  adaptColor (color) {
    // Simple color adaptation for theme
    if (this.isDarkTheme()) {
      // Lighten colors for dark theme
      return this.lightenColor(color, 20);
    }
    return color;
  }

  /**
   * Simple color lightening function
   */
  lightenColor (color, percent) {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return `#${(0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255))
      .toString(16).slice(1)}`;
  }

  /**
   * Reset theme to system default
   */
  resetToSystemTheme () {
    const systemTheme = this.detectSystemTheme();
    this.applyTheme(systemTheme);

    // Clear saved preference
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(this.themeStorageKey);
      }
    } catch (error) {
      console.warn('Could not clear theme preference:', error);
    }
  }

  /**
   * Get all available themes
   */
  getAvailableThemes () {
    return Object.keys(this.themes);
  }

  /**
   * Get theme transition duration for animations
   */
  getTransitionDuration () {
    return '300ms';
  }

  /**
   * Get theme transition timing function
   */
  getTransitionTimingFunction () {
    return 'ease-in-out';
  }
}
